import {
  LocalizationContextBuilder,
  MessageBundle,
  LocalizationContext,
} from "@amzn/arb-tools";
import { DEFAULT_LOCALE, MONS_META_TAGS } from "src/constants";
import translations from "translations";
import arbManifest from "translations/arbManifest";
import { monitor, METRIC_NAMES } from "src/utils/monitoring";

const LOCALIZATION_CONTEXTS = new Map<string, LocalizationContext>();
const HEADERS = {
  'Accept': 'application/json',
  'Content-Type': 'application/json',
};

export function getLocalizationContext(locale: string) {
  let lc = LOCALIZATION_CONTEXTS.get(locale);

  if (!lc) {
    lc = new LocalizationContextBuilder()
        .withLocale(locale)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build();

    LOCALIZATION_CONTEXTS.set(locale, lc);
  }

  return lc;
}

export function t(
    bundle: MessageBundle | undefined,
    stringId: string,
    args?: Record<string, unknown>
) {
  // MbmProvider fetches bundle async. So this condition will be hit on initial render when used with useBundle.
  if (!bundle || !stringId) {
    return "";
  }

  if (!args) {
    return bundle.getMessage(stringId);
  }

  return bundle.formatMessage(stringId, args);
}

export function getMbmOptions(arbManifest: {
  endpointMap: Record<string, Record<string, string>>;
}) {
  let currentLocale = DEFAULT_LOCALE;
  const documentLocale = document?.querySelector("html")!.getAttribute("lang");

  if (documentLocale) {
    currentLocale = documentLocale;
  }

  const localizationContext = getLocalizationContext(currentLocale);

  return {
    arbManifest,
    defaultLocalizationContext: localizationContext,
    resolveBundleUrl: (
        resourcePackPath: string | undefined,
        locale: string,
        bundle: string
    ) => resolveBundleUrlWithFallbacks(resourcePackPath, locale, bundle),
  };
}

export function resolveBundleUrlWithFallbacks(
    resourcePackPath: string | undefined,
    locale: string,
    bundle: string
) {
  if (resourcePackPath && translations[resourcePackPath]) {
    return translations[resourcePackPath];
  }

  if (!resourcePackPath && arbManifest.endpointMap?.[locale]?.[bundle]) {
    return translations[arbManifest.endpointMap[locale][bundle]];
  }

  if (arbManifest.endpointMap?.[DEFAULT_LOCALE]?.[bundle]) {
    return translations[arbManifest.endpointMap[DEFAULT_LOCALE][bundle]];
  }

  return undefined;
}

export function getMessageFromError(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  } else if (typeof error === "string") {
    return error;
  } else {
    // Best effort, coerce unkown type to string.
    return JSON.stringify(error);
  }
}

/** Supported HTTP methods */
export enum HttpMethod {
  /* eslint-disable no-unused-vars */
  GET = 'GET',
  POST = 'POST',
  /* eslint-enable no-unused-vars */
}

/** Options for the monitoredFetch function */
export interface MonitoredFetchOptions {
  /** The URL to fetch */
  url: string;
  /**
   * Logical operation name used as the `operation` metric dimension.
   *
   * Use a stable, low-cardinality identifier (e.g. "GetCampaignDetails",
   * "CreateCampaign"). DO NOT pass raw URLs here — the URL may contain
   * campaign UUIDs or other unbounded values, which would explode metric
   * cardinality and cost in CloudWatch.
   */
  operation: string;
  /** Request body (will be JSON-stringified) */
  input?: unknown;
  /** HTTP method (defaults to HttpMethod.GET) */
  method?: HttpMethod | string;
  /** Custom headers (defaults to JSON Accept/Content-Type) */
  headers?: Record<string, string>;
  /** Optional AbortSignal to cancel the in-flight HTTP request */
  signal?: AbortSignal;
}

/**
 * Performs a monitored fetch request with automatic metrics recording and error handling.
 *
 * @template T - The expected return type of the response
 * @param options - Fetch options including url, input, method, and headers
 * @returns Promise resolving to the parsed response data
 */
export async function monitoredFetch<T = unknown>({ url, operation, input, method = 'GET', headers = HEADERS, signal }: MonitoredFetchOptions): Promise<T> {
  method = method.toUpperCase();

  const apiMetric = monitor.recordStatMetrics(
      METRIC_NAMES.API_FETCH,
      { operation, methodName: "FetchApi" },
  );
  let apiStatus = 200;
  try {
    let response: Response;

    if (method === HttpMethod.GET) {
      response = await fetch(url, { headers, signal });
    } else {
      // All non-GET methods (POST, etc.) require a CSRF token
      const csrfToken = getMetaContent(MONS_META_TAGS.CSRF_TOKEN);
      if (!csrfToken) {
        throw new Error(`CSRF token is required for ${method} requests but was not found.`);
      }

      response = await fetch(url, {
        method,
        headers: {
          'anti-csrftoken-a2z': csrfToken,
          ...headers,
        },
        body: JSON.stringify(input),
        signal,
      });
    }

    if (!response.ok) {
      apiStatus = response.status;
      throw new Error(
          `status: ${apiStatus} | statusText: ${response.statusText}`
      );
    }

    let data: T;
    // Return parsed JSON response if Content-Type is JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = (await response.text()) as unknown as T;
    }

    // Log success metric for the API
    apiMetric.success();
    return data;
  } catch (error) {
    // A client-initiated cancel (component unmount, or tab switch) is not a service failure.
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    // For 4XX, suffix metricName with Error, for others append Fault
    if (apiStatus >= 400 && apiStatus < 500) {
      apiMetric.error();
    } else {
      apiMetric.fault();
    }
    // This will be logged in our error boundary.
    throw new Error(
        `[fetch-exception] endpoint: ${url} | message: ${getMessageFromError(
            error
        )}`
    );
  }
}

/**
 * Parses a string into an optional number.
 * Returns `undefined` for empty strings or non-numeric values,
 * making it safe for form inputs that map to optional numeric fields.
 *
 * @param value - The string value to parse
 * @returns The parsed number, or undefined if the value is empty or not a valid number
 */
export const parseOptionalNumber = (value: string): number | undefined => {
    if (value.trim() === '') return undefined;
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
};

export function getMetaContent(name: string): string | undefined {
  const element = document.querySelector(`[name="${name}"]`) as HTMLElement;

  if (!element || !element.getAttribute("content")) {
    return undefined;
  }

  return element.getAttribute("content") as string;
}